import { useEffect, useRef, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, CircularProgress, Paper } from '@mui/material';
import { Product } from '../../interfaces/Product/Product';
import { getProduct } from '../../api/Product';
import { ProductOptionValue } from '../../interfaces/Product/ProductOption';
import { OptionDropdown } from '../Dropdowns/OptionDropdown';
import { QuantityDropdown } from '../Dropdowns/QuantityDropdown';
import { addItemToBasket } from '../../utils/Basket';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useBasket } from '../../hooks/useBasket';
import { useAnalytics } from '../../analytics/useAnalytics';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type AddToBasketPopperProps = {
  productSlug: string;
  atCheckout: boolean;
  forcedQuantity?: number;
  onClose: () => void;
};

export const AddToBasketPopper = ({
  productSlug,
  atCheckout,
  forcedQuantity,
  onClose,
}: AddToBasketPopperProps) => {
  const t = useLocalizedText();

  const { primary, secondary } = useThemeColor();
  const { addToCart: trackAddToCart } = useAnalytics();

  const { refreshBasket } = useBasket();

  const paperRef = useRef<HTMLDivElement>(null);

  const [product, setProduct] = useState<Product | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<
    ProductOptionValue | undefined
  >(undefined);
  const [selectedAmount, setSelectedAmount] = useState(forcedQuantity ?? 1);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const selectDropdown = document.querySelector('.MuiPopover-root');
      const isInsideDropdown = selectDropdown?.contains(target);

      if (
        paperRef.current &&
        !paperRef.current.contains(target) &&
        !isInsideDropdown
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    setLoading(true);
    setSelectedOption(undefined);

    getProduct(productSlug)
      .then((p) => {
        setProduct(p);
        setSelectedOption(p.options[0]?.values[0]);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [productSlug]);

  useEffect(() => {
    setSelectedAmount(forcedQuantity ?? 1);
  }, [product, selectedOption, forcedQuantity]);

  const handleAddToBasket = () => {
    if (!product) {
      return;
    }

    setLoading(true);
    addItemToBasket({
      productID: product.id,
      quantity: selectedAmount,
      optionValueID: selectedOption?.id,
      addedAtCheckout: atCheckout,
    })
      .then(() => {
        refreshBasket();
        toast.success(t('product.successful_add_to_cart'));
        trackAddToCart({
          id: product.id,
          quantity: selectedAmount,
          unitPrice: product.specialPrice?.amount ?? product.price.amount,
        });
      })
      .catch((err) => toast.error(err.message))
      .finally(() => {
        onClose();
        setLoading(false);
      });
  };

  const quantity = selectedOption?.stock ?? product?.qty;

  return (
    <Paper
      ref={paperRef}
      onClick={(ev) => {
        ev.stopPropagation();
        ev.preventDefault();
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300,
        minWidth: 300,
        p: 1.5,
        gap: 1.5,
      }}
    >
      {loading && <CircularProgress color='primary' />}

      {product?.options.map((option) => (
        <OptionDropdown
          key={option.id}
          option={option}
          value={selectedOption}
          onChange={setSelectedOption}
        />
      ))}

      {!!quantity && (
        <QuantityDropdown
          availableQuantity={quantity ?? 0}
          value={selectedAmount}
          onChange={setSelectedAmount}
          disabled={atCheckout || Number.isFinite(forcedQuantity)}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 0.5,
          width: '100%',
          justifyContent: 'end',
        }}
      >
        <Button
          disabled={!quantity}
          onClick={handleAddToBasket}
          variant='contained'
          sx={{
            textTransform: 'none',
            backgroundColor: primary,
            color: secondary,
          }}
        >
          {t('common.confirm')}
        </Button>

        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: primary,
          }}
        >
          {t('common.cancel')}
        </Button>
      </Box>
    </Paper>
  );
};
